import { Link } from "@tanstack/react-router";
import { CheckCircle2, MapPin, PawPrint, AlertTriangle } from "lucide-react";
import type { Cause, Institution } from "@/project/data/sample";
import { formatINR, getInstitution, progressPct } from "@/project/data/sample";

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "verified" | "urgent" | "critical" | "featured" | "completed" | "neutral";
  children: React.ReactNode;
}) {
  const styles: Record<string, string> = {
    verified: "bg-[oklch(0.93_0.06_155)] text-[var(--success)]",
    urgent: "bg-[oklch(0.94_0.06_60)] text-[var(--saffron-deep)]",
    critical: "bg-[oklch(0.94_0.06_25)] text-[var(--maroon)]",
    featured: "bg-[oklch(0.94_0.05_40)] text-[var(--maroon-deep)]",
    completed: "bg-[oklch(0.94_0.02_70)] text-foreground/70",
    neutral: "bg-[var(--cream-2)] text-foreground/70",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${styles[tone]}`}
    >
      {children}
    </span>
  );
}

export function InstitutionCard({ inst }: { inst: Institution }) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:shadow-[var(--shadow-card)] hover:-translate-y-0.5">
      <Link to="/institutions/$id" params={{ id: inst.id }} className="relative aspect-[16/10] overflow-hidden">
        <img
          src={inst.cover}
          alt={inst.name}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex gap-2">
          {inst.verified && (
            <Badge tone="verified">
              <CheckCircle2 className="h-3 w-3" /> Verified
            </Badge>
          )}
          {inst.has80G && <Badge tone="featured">80G</Badge>}
        </div>
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <p className="text-xs uppercase tracking-wider text-foreground/70">{inst.type}</p>
          <Link to="/institutions/$id" params={{ id: inst.id }}>
            <h3 className="mt-1 font-display text-lg font-semibold leading-snug hover:text-[var(--maroon)] transition">{inst.name}</h3>
          </Link>
        </div>

        <p className="flex items-center gap-1.5 text-xs text-foreground/60">
          <MapPin className="h-3.5 w-3.5" /> {inst.city}, {inst.state}
        </p>

        <div className="mt-auto flex items-center justify-between border-t border-border/60 pt-3 text-xs">
          <span className="flex items-center gap-1.5 text-foreground/70">
            <PawPrint className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />
            {inst.animalCount.toLocaleString("en-IN")} animals
          </span>

          <Link
            to="/donate"
            search={{ institution: inst.id } as never}
            className="rounded-full bg-[var(--maroon)] px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-[var(--maroon-deep)] transition"
          >
            Direct Seva
          </Link>
        </div>
      </div>
    </div>
  );
}

export function CauseCard({ cause }: { cause: Cause }) {
  const inst = getInstitution(cause.institutionId);
  const pct = progressPct(cause);
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:shadow-[var(--shadow-card)] hover:-translate-y-0.5">
      <Link to="/causes/$id" params={{ id: cause.id }} className="relative aspect-[16/10] overflow-hidden">
        <img
          src={cause.image}
          alt={cause.title}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex gap-2">
          {cause.urgency === "Critical" && (
            <Badge tone="critical"><AlertTriangle className="h-3 w-3" /> Critical</Badge>
          )}
          {cause.urgency === "Urgent" && <Badge tone="urgent">Urgent</Badge>}
          {cause.featured && <Badge tone="featured">Featured</Badge>}
        </div>
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <p className="text-xs uppercase tracking-wider text-foreground/70">
            {cause.category} · {inst?.city}
          </p>
          <Link to="/causes/$id" params={{ id: cause.id }}>
            <h3 className="mt-1 font-display text-lg font-semibold leading-snug hover:text-[var(--maroon)]">
              {cause.title}
            </h3>
          </Link>
          <p className="mt-1 text-xs text-foreground/60">by {inst?.name}</p>
        </div>
        <p className="text-sm text-foreground/75 line-clamp-2">{cause.shortDesc}</p>
        <div>
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-semibold text-[var(--maroon-deep)]">{formatINR(cause.raised)}</span>
            <span className="text-foreground/70">raised of {formatINR(cause.required)}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
            <div
              className="h-full rounded-full"
              style={{ width: `${pct}%`, background: "var(--gradient-warm)" }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] text-foreground/70">
            <span>{pct}% funded</span>
            <span>{cause.donors} donors</span>
          </div>
        </div>
        <Link
          to="/donate"
          search={{ cause: cause.id }}
          className="mt-1 inline-flex justify-center rounded-full bg-[var(--maroon)] px-4 py-2 text-sm font-semibold text-[var(--cream)] hover:bg-[var(--maroon-deep)] transition"
        >
          Donate Now
        </Link>
      </div>
    </div>
  );
}